import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Keyboard } from 'lucide-react';

// Mirrors the bindings wired in /app/frontend/src/hooks/useTallyShortcuts.js.
// Keeping the source of truth there and the cheat-sheet here separate is
// intentional — this dialog is human-facing copy, not configuration.
const SHORTCUT_GROUPS = [
  {
    title: 'Voucher navigation',
    items: [
      { keys: ['F8'], action: 'Open Sales voucher (Invoices)' },
      { keys: ['F9'], action: 'Open Purchase voucher (Purchase invoices)' },
      { keys: ['Esc'], action: 'Close active dialog or jump to Dashboard' },
    ],
  },
  {
    title: 'Document actions',
    items: [
      { keys: ['Ctrl', 'A'], action: 'Accept / Save the open document' },
      { keys: ['Alt', 'P'], action: 'Print the current document' },
    ],
  },
  {
    title: 'Inline master creation',
    items: [
      {
        keys: ['Alt', 'C'],
        action:
          'While focused on a party/product combobox, open the "Add new …" dialog for that master',
      },
    ],
  },
];

function Key({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-md border border-slate-300 bg-slate-50 text-[11px] font-semibold text-slate-700 shadow-[inset_0_-1px_0_rgba(0,0,0,0.06)]">
      {children}
    </kbd>
  );
}

export function KeyboardShortcutsDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="keyboard-shortcuts-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Keyboard className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Tally-compatible bindings. Work everywhere in the app, except inside text
            inputs (so typing in a Notes field is unaffected).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                {group.title}
              </div>
              <div className="space-y-1.5">
                {group.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between gap-4 px-3 py-2 rounded-md hover:bg-slate-50"
                  >
                    <span className="text-sm text-slate-700 leading-relaxed flex-1">
                      {item.action}
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {item.keys.map((k, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-slate-400 text-xs">+</span>}
                          <Key>{k}</Key>
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-100">
          Tip: bindings auto-disable inside text inputs and textareas so accountants
          editing notes are never surprised.
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsDialog;
